import { createContext, useContext, useMemo } from "react";
import { IOverviewResponse, IMonthlyRequestCount, IMonthlyAvgEndToEndDays } from "../../../interfaces/CloseTheLoop.interfaces";

// Theme colors - shared across all Overview components
export const THEME = {
    primary: "#0078D4",
    primaryLight: "#2B88D8",
    primaryDark: "#005A9E",
    background: "#F5F7FA",
    surface: "#FFFFFF",
    textPrimary: "#1A1A2E",
    textSecondary: "#5A5A72",
    border: "#E1E5EB",
    success: "#107C10",
    warning: "#FFB900",
    error: "#D13438",
    successBg: "#DFF6DD",
    warningBg: "#FFF4CE",
    errorBg: "#FDE7E9"
};

// Date range options for filtering
export type DateRangeOption = "2years" | "all" | "custom";

// Custom date range interface
export interface CustomDateRange {
    startDate: Date | null;
    endDate: Date | null;
}

// Context for sharing overview data across components
export interface OverviewContextType {
    data: IOverviewResponse | undefined;
    isLoading: boolean;
    selectedProducts: string[];
    dateRange: DateRangeOption;
    setDateRange: (range: DateRangeOption) => void;
    customDateRange: CustomDateRange;
    setCustomDateRange: (range: CustomDateRange) => void;
    filteredMonthlyRequestCounts: IMonthlyRequestCount[];
    filteredMonthlyAvgEndToEndDays: IMonthlyAvgEndToEndDays[];
    totalDataYears: number;
}

export const OverviewContext = createContext<OverviewContextType>({
    data: undefined,
    isLoading: true,
    selectedProducts: [],
    dateRange: "2years",
    setDateRange: () => { },
    customDateRange: { startDate: null, endDate: null },
    setCustomDateRange: () => { },
    filteredMonthlyRequestCounts: [],
    filteredMonthlyAvgEndToEndDays: [],
    totalDataYears: 0
});

export const useOverviewData = () => useContext(OverviewContext);

// Helper function to generate time range label based on dateRange and customDateRange
export const getTimeRangeLabel = (dateRange: DateRangeOption, customDateRange?: CustomDateRange): string => {
    if (dateRange === "2years") return "Last 2 years";
    if (dateRange === "custom" && customDateRange?.startDate && customDateRange?.endDate) {
        const formatDate = (d: Date) => d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
        return `${formatDate(customDateRange.startDate)} - ${formatDate(customDateRange.endDate)}`;
    }
    return "All time";
};

// Utility: Filter monthly data to show only last N years
export const filterByDateRange = <T extends { year: number; month: string }>(
    data: T[],
    dateRange: DateRangeOption
): T[] => {
    if (dateRange === "all" || !data?.length) return data;

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed
    const cutoffYear = currentYear - 2;
    const cutoffMonth = currentMonth;

    const monthMap: Record<string, number> = {
        "Jan": 0, "Feb": 1, "Mar": 2, "Apr": 3, "May": 4, "Jun": 5,
        "Jul": 6, "Aug": 7, "Sep": 8, "Oct": 9, "Nov": 10, "Dec": 11
    };

    return data.filter((item) => {
        const itemMonth = monthMap[item.month] ?? 0;
        // Include if after cutoff date (year > cutoffYear, or same year but month >= cutoffMonth)
        if (item.year > cutoffYear) return true;
        if (item.year === cutoffYear && itemMonth >= cutoffMonth) return true;
        return false;
    });
};

// Hook to compute filtered data
export const useFilteredOverviewData = (
    data: IOverviewResponse | undefined,
    dateRange: DateRangeOption
) => {
    const filteredMonthlyRequestCounts = useMemo(() => {
        return filterByDateRange(data?.monthlyRequestCounts ?? [], dateRange);
    }, [data?.monthlyRequestCounts, dateRange]);

    const filteredMonthlyAvgEndToEndDays = useMemo(() => {
        return filterByDateRange(data?.monthlyAvgEndToEndDays ?? [], dateRange);
    }, [data?.monthlyAvgEndToEndDays, dateRange]);

    const totalDataYears = useMemo(() => {
        if (!data?.monthlyRequestCounts?.length) return 0;
        const years = new Set(data.monthlyRequestCounts.map((m) => m.year));
        return years.size;
    }, [data?.monthlyRequestCounts]);

    return {
        filteredMonthlyRequestCounts,
        filteredMonthlyAvgEndToEndDays,
        totalDataYears
    };
};
